import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import IndexedDBManager from '../../utils/indexedDB/manager';
import Utils from '../../utils';
import RoutesUtils from '../../utils/routes';
import { DriveItemListOrigin } from '../DriveItemListPage/constants';

export enum SelectionType {
	RESOURCES = 'resources',
	NOTES = 'notes',
}

export enum SelectionActionKeys {
	TOGGLE_SELECTION = '@@selection/TOGGLE_SELECTION',
	LOAD_SELECTION = '@@selection/LOAD_SELECTION',
	REMOVE_FROM_SELECTION = '@@selection/REMOVE_FROM_SELECTION',
	CLEAR_SELECTION = '@@selection/CLEAR_SELECTION',
}

export type SelectionListAction =
	ToggleSelectionsAction |
	LoadSelectionAction |
	RemoveFromSelectionAction |
	ClearSelectionAction;

interface ToggleSelectionsAction extends Action {
	type: SelectionActionKeys.TOGGLE_SELECTION;
	selectionIds: number[];
	sourceType: SelectionType;
}

interface LoadSelectionAction extends Action {
	type: SelectionActionKeys.LOAD_SELECTION;
	selectionIds: number[];
	sourceType: SelectionType;
}

interface RemoveFromSelectionAction extends Action {
	type: SelectionActionKeys.REMOVE_FROM_SELECTION;
	selectionIds: number[];
	sourceType: SelectionType;
}

interface ClearSelectionAction extends Action {
	type: SelectionActionKeys.CLEAR_SELECTION;
	sourceType: SelectionType;
}

export const loadPreviousSelection = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const [resourcesSelection, notesSelection] = await Promise.all([
		IndexedDBManager.getSelection(),
		IndexedDBManager.getNotesSelection(),
	]);

	const prevSelection = {
		[SelectionType.RESOURCES]: resourcesSelection,
		[SelectionType.NOTES]: notesSelection,
	};

	Object.entries(prevSelection).forEach(([selectionType, ids]) => {
		if (ids) {
			const action: LoadSelectionAction = {
				type: SelectionActionKeys.LOAD_SELECTION,
				selectionIds: ids,
				sourceType: selectionType as SelectionType,
			};
			dispatch(action);
		}
	});
};

export const toggleSelection = (itemId: number, sourceType: SelectionType = SelectionType.RESOURCES) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		selection,
		routerParamsHistory,
	} = getState();
	const ids = selection[sourceType];
	const isInSelection = ids.includes(itemId);
	let newSelection = [...ids];

	if (isInSelection) {
		newSelection.splice(newSelection.indexOf(itemId), 1);

		if (
			RoutesUtils.currentLocationIs(routerParamsHistory, DriveItemListOrigin.Selection) &&
			sourceType === SelectionType.NOTES &&
			Utils.isSharedNote(itemId)
		) {
			IndexedDBManager.removeNote(itemId);
		}
	} else {
		newSelection.push(itemId);
	}

	if (sourceType === SelectionType.RESOURCES) {
		IndexedDBManager.setSelection(newSelection).then();
	} else {
		IndexedDBManager.setNotesSelection(newSelection).then();
	}

	const action: ToggleSelectionsAction = {
		type: SelectionActionKeys.TOGGLE_SELECTION,
		selectionIds: newSelection,
		sourceType,
	};

	dispatch(action);
};

export const removeFromSelection = (
	idsToRemove: number[],
	sourceType: SelectionType = SelectionType.RESOURCES
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { selection } = getState();
	const ids = selection[sourceType];
	const newSelection = ids.filter(id => !idsToRemove.includes(id));

	if (sourceType === SelectionType.RESOURCES) {
		IndexedDBManager.setSelection(newSelection).then();
	} else {
		IndexedDBManager.setNotesSelection(newSelection).then();
	}

	const action: RemoveFromSelectionAction = {
		type: SelectionActionKeys.REMOVE_FROM_SELECTION,
		selectionIds: newSelection,
		sourceType,
	};

	dispatch(action);
};

export const clearSelection = (selectionTypeToClear?: SelectionType) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const selectionStorageHandlers = {
		[SelectionType.RESOURCES]: async (ids: number[]) => await IndexedDBManager.setSelection(ids),
		[SelectionType.NOTES]: async (ids: number[]) => await IndexedDBManager.setNotesSelection(ids),
	};

	for (let i = 0; i < Object.values(SelectionType).length; i++) {
		const selectionType = Object.values(SelectionType)[i];

		if (!selectionTypeToClear || selectionType === selectionTypeToClear) {
			if (selectionType === SelectionType.NOTES) {
				await dispatch(removeSharedNotes());
			}

			const action: ClearSelectionAction = {
				type: SelectionActionKeys.CLEAR_SELECTION,
				sourceType: selectionType,
			};
			dispatch(action);

			selectionStorageHandlers[selectionType]([]);
		}
	}
};

const removeSharedNotes = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { selection: { notes } } = getState();

	for (let i = 0; i < notes.length; i++) {
		const noteId = notes[i];

		if (Utils.isSharedNote(noteId)) {
			await IndexedDBManager.removeNote(noteId);
		}
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Selection/actions.ts